export const tableBodyProp = {
  '& tr:hover td': {
    // No-op fallback for non-hover-tables (gets overridden by
    // _bodyHover below when the table opts in). Token-driven so
    // dark theme picks up the correct surface.
    backgroundColor: 'var(--qhs-surface)',
    transition: 'background-color 200ms ease-in, font-weight 200ms ease-in',
  },
  '& td': {
    border: 'none !important',
    // Default body cell surface — was hardcoded `#ffffff` which
    // forced a white floor in dark theme. Token swaps to the
    // correct dark surface automatically.
    backgroundColor: 'var(--qhs-surface)',
    transition: 'background-color 200ms ease-in, font-weight 200ms ease-in',
  },
  // Row state highlights for alerts / unread / error rows.
  //
  // Severity-aware palette: cool BRAND-BLUE for info (semantically
  // matches links / info messaging in the rest of the app) and warm
  // ERROR-RED for critical. Blue vs red is the maximum hue distance
  // available in our palette — a quick glance instantly tells the
  // operator "informational" vs "critical" without reading the
  // status column. We tried Warning Orange for info first (warmer
  // and arguably more "attention-y"), but at low alpha orange and
  // red are both warm-cream and blur together — severity is no
  // longer readable.
  //
  // Tint intensity:
  //   - new (non-hover): 10 % over white  -> `#eaedfb` / `#fce7e7`
  //   - new hover:       16 % over white  -> `#dee2f8` / `#f9d9d9`
  //   - viewed:           5 % over white  -> `#f5f6fd` / `#fdf3f3`
  //
  // Calibrated against the original cheap-saturated `#aed2ff` (~25 %)
  // and the previous attempt at `~7 %` which was below the noise
  // threshold on tables full of flagged rows. Text contrast at the
  // deepest tint (Brand 16 % over white) is still ~13:1 against
  // `--qhs-text-1` — well above WCAG AA.
  //
  // Backgrounds are SOLID hex (not rgba alpha) so pinned columns —
  // which use `position: sticky` and let unpinned columns scroll
  // behind them — get an opaque header band that doesn't expose
  // body content through it.
  //
  // Bold font weight on new rows is preserved as a third skim cue.
  //
  // **Keep these in sync with `shared/ui/Table/legacy-row-state.css`.**
  // That stylesheet is loaded globally via `ViewedTableCell`'s
  // side-effect import and uses `.mantine-Table-root .hoverNewRow td`
  // selectors that match every Mantine table on the page — so its
  // values, not these, are what the user actually sees today. The two
  // definitions must agree.
  '& .hoverNewRow:hover td': {
    backgroundColor: 'var(--qhs-row-new-hover) !important',
    transition: 'background-color 300ms ease-in, font-weight 300ms ease-in',
  },
  '& .hoverNewRow td': {
    backgroundColor: 'var(--qhs-row-new) !important',
    fontWeight: 'bold !important',
    transition: 'background-color 300ms ease-in, font-weight 300ms ease-in',
  },
  '& .hoverViewedRow:hover td': {
    backgroundColor: 'var(--qhs-row-viewed) !important',
    transition: 'background-color 300ms ease-in, font-weight 300ms ease-in',
  },
  '& .hoverNewErrorRow:hover td': {
    backgroundColor: 'var(--qhs-row-new-error-hover) !important',
    transition: 'background-color 300ms ease-in, font-weight 300ms ease-in',
  },
  '& .hoverNewErrorRow td': {
    backgroundColor: 'var(--qhs-row-new-error) !important',
    fontWeight: 'bold !important',
    transition: 'background-color 300ms ease-in, font-weight 300ms ease-in',
  },
  '& .hoverViewedErrorRow td': {
    backgroundColor: 'var(--qhs-row-viewed-error) !important',
    transition: 'background-color 300ms ease-in, font-weight 300ms ease-in',
  },

  '& tr:has(.activeCellLink:hover) > td': {
    backgroundColor: 'var(--qhs-surface)',
    transition: '0.3s',
    cursor: 'pointer',
  },
  '& tr:has(.activeCellLink:hover) > td .hoverNewRow': {
    backgroundColor: 'var(--qhs-row-new-hover) !important',
    transition: '0.3s',
  },
  '& tr:has(.activeCellLink:hover) > td .hoverViewedRow': {
    backgroundColor: 'var(--qhs-row-viewed) !important',
    transition: '0.3s',
  },
  '& tr:has(.activeCellLink:hover) > td .hoverNewErrorRow': {
    backgroundColor: 'var(--qhs-row-new-error-hover) !important',
    transition: '0.3s',
  },
};

const _bodyHover = {
  '& tr:hover td': {
    // Token-driven hover surface — was hardcoded `#f1f3f5`.
    // `--qhs-surface-hover` is calibrated for both themes.
    backgroundColor: 'var(--qhs-surface-hover)',
    transition: 'background-color 200ms ease-in, font-weight 200ms ease-in',
  },
}

export const tableBodyHoverProp = {
  ...tableBodyProp,
  ..._bodyHover,
}

export const loadingOverlayProps = {
  display: 'none',
};

export const tableHeadCellProps = {
  sx: {
    '& .mantine-TableHeadCell-Content-Labels': {
      gap: 6,

      '& .mantine-TableHeadCell-Content-Wrapper': {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        display: 'block',
      },
    },
  },
};

export const skeletonProps = {
  borderRadius: 10,
  backgroundColor: 'red',

  sx: {
    '& .mantine-Skeleton-visible': {
      borderRadius: 10,
      display: 'none',
      backgroundColor: 'red',
    },
    '& .mantine-Skeleton-root': {
      borderRadius: 10,
      display: 'none',
      backgroundColor: 'red',
    },
  },
};

export const TABLE_DEFAULT_SETTINGS = {
  enablePagination: false,
  enableRowNumbers: false,
  enableRowVirtualization: true,
  enableColumnResizing: true,
  enablePinning: true,
  enableClickToCopy: false,
  enableColumnOrdering: true,
  positionActionsColumn: 'last',
  columnVirtualizerProps: { overscan: 1 },
  enableStickyHeader: true,
  enableBottomToolbar: false,
  enableGlobalFilter: false,
  enableFilterMatchHighlighting: false,
  selectAllMode: 'all',
  getRowProps: {},
  editDisplayMode: 'table',
  getRowId: (originalRow) => originalRow.uuid,
  mantineTableHeadCellProps: tableHeadCellProps,
  mantineLoadingOverlayProps: loadingOverlayProps,
  mantineSkeletonProps: skeletonProps,
  // Keyboard tab order: the drag grip is a pointer-only gesture and the
  // column-actions menu duplicates the table-settings surface — both
  // are noise as tab stops between the header sort control and the
  // filter input. Drop them from the sequence (they stay mouse-usable);
  // sorting keeps its tab stop via the header label.
  mantineColumnDragHandleProps: { tabIndex: -1 },
  mantineColumnActionsButtonProps: { tabIndex: -1 },
};
